import { useState } from 'react';

import { DemographicInsightPresentationMode } from 'modules/domains/segmentation';
import { ContactProperty } from 'modules/domains/user';
import { BaseOption } from 'ui/selects';
import { pxToRem } from 'utils/convert';
import { SegmentedControlOption } from 'ui/segmentedControl';
import { InsightSelect } from '../insightSelect/InsightSelect';

import { useCustomDemographicContext } from '../../../contexts/demographicInsights';
import { useTranslation } from '../../../hooks';

const contactPropertySelectWidth = pxToRem(350);

const formatToSelectOption = (fields: ContactProperty[]): BaseOption<string>[] =>
  fields.map(({ name, type }) => ({
    id: `contact-property-${name}-${type}`,
    label: name,
    value: name,
    Suffix: type,
  }));

const selectMediumOptions: SegmentedControlOption[] = [
  { name: 'Show with chart', value: DemographicInsightPresentationMode.TableAndChart },
  { name: 'Show as table', value: DemographicInsightPresentationMode.Table },
];

export const CONTACT_PROPERTY_DATA_TEST = {
  toggle: 'contact-property-toggle',
  select: 'contact-property-select',
};

export const ContactPropertySelect = () => {
  const { t } = useTranslation();
  const {
    addDemographicContactProperty,
    cachedCustomDemograhpicInsights,
    contactProperties,
    maxDemographicInsightSettingsForProject,
  } = useCustomDemographicContext();

  // we always want the default presentation view to the table and chart;
  const [isTableAndChart, updateMode] = useState<boolean>(true);
  const presentationMode = isTableAndChart
    ? DemographicInsightPresentationMode.TableAndChart
    : DemographicInsightPresentationMode.Table;
  const toggleMode = (mode: DemographicInsightPresentationMode) => {
    if (mode !== presentationMode) {
      updateMode((previous) => !previous);
    }
  };

  const options = formatToSelectOption(contactProperties);
  const placeholder = t('insights.select.contactPropertyPlaceholder');
  const searchPlaceholder = t('insights.select.filterPlaceholder');
  const isMaxPropertiesSelected =
    !!cachedCustomDemograhpicInsights &&
    cachedCustomDemograhpicInsights.length === maxDemographicInsightSettingsForProject;

  return (
    <InsightSelect
      isDisabled={isMaxPropertiesSelected}
      selectTestId={CONTACT_PROPERTY_DATA_TEST.select}
      selectPlaceholder={placeholder}
      selectOptions={options}
      onOptionSelect={([value]) => addDemographicContactProperty(`${value}`, presentationMode)}
      segmentedControlTestId={CONTACT_PROPERTY_DATA_TEST.toggle}
      segmentedControlMode={presentationMode}
      segmentedControlOptions={selectMediumOptions}
      onSegmentedControlSelect={toggleMode}
      textInputPlaceholder={searchPlaceholder}
      width={contactPropertySelectWidth}
    />
  );
};
